import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { scoreSortRound, type Difficulty } from "@hunch/core";
import { GAME_BRANDING } from "@hunch/branding";
import { useI18n } from "../../i18n/index.js";
import { SortEngine } from "../../components/game/SortEngine.js";
import { CountdownBar } from "../../components/game/CountdownBar.js";
import { PointsBurst } from "../../components/game/PointsBurst.js";
import type { RoundSummary } from "../../components/game/EndScreen.js";
import { ROUND_TIME_SECONDS } from "../../lib/roundTime.js";
import { RevealList, type RevealEntry } from "./RevealList.js";
import type { TimeslipCard } from "./api.js";
import styles from "./TimeslipPlay.module.css";

interface TimeslipPlayProps {
  rounds: TimeslipCard[][];
  pointsPerCard: number;
  difficulty: Difficulty;
  onFinish: (roundSummaries: RoundSummary[], totalScore: number) => void;
}

const TIMEOUT_ADVANCE_DELAY_MS = 3000;

function ranksByYear(cards: TimeslipCard[]): Map<number, number> {
  const sorted = cards.slice().sort((a, b) => a.year - b.year);
  const ranks = new Map<number, number>();
  sorted.forEach((card, i) => ranks.set(card.id, i + 1));
  return ranks;
}

export function TimeslipPlay({ rounds, pointsPerCard, difficulty, onFinish }: TimeslipPlayProps) {
  const { lang, t } = useI18n();
  const roundLimit = ROUND_TIME_SECONDS[difficulty];
  const [roundIndex, setRoundIndex] = useState(0);
  const [order, setOrder] = useState<TimeslipCard[]>(rounds[0] ?? []);
  const [checked, setChecked] = useState(false);
  const [collected, setCollected] = useState<RoundSummary[]>([]);
  const [timeLeft, setTimeLeft] = useState<number | null>(roundLimit);
  const [timedOut, setTimedOut] = useState(false);

  const ranks = useMemo(() => ranksByYear(rounds[roundIndex] ?? []), [rounds, roundIndex]);

  const result = useMemo(() => {
    if (!checked) return null;
    const truePositions = order.map((c) => ranks.get(c.id)!);
    return scoreSortRound(truePositions, pointsPerCard);
  }, [checked, order, ranks, pointsPerCard]);

  // Per-round countdown (spec extension, see docs/decisions.md): ticks once
  // per second while the player is still sorting. Pauses once checked.
  useEffect(() => {
    if (roundLimit === null || checked) return;
    const id = window.setInterval(() => {
      setTimeLeft((prev) => {
        if (prev === null) return null;
        if (prev <= 1) {
          setTimedOut(true);
          setChecked(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => window.clearInterval(id);
  }, [checked, roundIndex, roundLimit]);

  function handleCheck() {
    setChecked(true);
  }

  function handleNext() {
    if (!result) return;
    const summary: RoundSummary = {
      points: result.totalPoints,
      maxPoints: pointsPerCard * order.length,
      greenSquares: result.greenIndices.length,
      totalSquares: order.length,
    };
    const next = [...collected, summary];

    if (roundIndex + 1 >= rounds.length) {
      onFinish(
        next,
        next.reduce((sum, r) => sum + r.points, 0),
      );
      return;
    }

    setCollected(next);
    setRoundIndex(roundIndex + 1);
    setOrder(rounds[roundIndex + 1] ?? []);
    setChecked(false);
    setTimedOut(false);
    setTimeLeft(roundLimit);
  }

  // Timed-out rounds auto-advance after a short pause so the player still
  // gets to see the reveal — a manual "Weiter" click in the meantime just
  // advances immediately and this cleanup cancels the pending auto-advance.
  useEffect(() => {
    if (!timedOut || !checked) return;
    const id = window.setTimeout(handleNext, TIMEOUT_ADVANCE_DELAY_MS);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timedOut, checked, roundIndex]);

  const runningTotal =
    collected.reduce((sum, r) => sum + r.points, 0) + (checked && result ? result.totalPoints : 0);

  const revealEntries: RevealEntry[] = useMemo(() => {
    if (!result) return [];
    const greenSet = new Set(result.greenIndices);
    return order.map((card, i) => ({
      card,
      isGreen: greenSet.has(i + 1),
      truePosition: ranks.get(card.id)!,
      points: pointsPerCard,
    }));
  }, [result, order, ranks, pointsPerCard]);

  const correctOrder = useMemo(
    () => (rounds[roundIndex] ?? []).slice().sort((a, b) => a.year - b.year),
    [rounds, roundIndex],
  );

  return (
    <main className={styles.main}>
      <div className={styles.statusRow}>
        <span className={styles.progress}>
          {GAME_BRANDING.timeslip.name} ·{" "}
          {t("timeslip.play.roundOf", { current: roundIndex + 1, total: rounds.length })}
        </span>
        <span className={styles.totalScore}>{t("timeslip.play.totalScore", { total: runningTotal })}</span>
      </div>
      {timeLeft !== null && roundLimit !== null && (
        <CountdownBar secondsLeft={timeLeft} secondsTotal={roundLimit} />
      )}
      {timedOut && <div className={styles.timeUpBanner}>{t("timeslip.play.timeUp")}</div>}

      {!checked && (
        <SortEngine
          items={order}
          getId={(c) => String(c.id)}
          renderLabel={(c) => (lang === "de" ? c.textDe : c.textEn)}
          onChange={setOrder}
          topLabel={t("timeslip.play.topLabel")}
          bottomLabel={t("timeslip.play.bottomLabel")}
          dragHandleLabel={t("timeslip.play.dragHandle")}
        />
      )}

      {checked && result && (
        <>
          <PointsBurst points={result.totalPoints} triggerKey={roundIndex} />
          <p className={styles.summary}>
            {t("timeslip.play.summaryLine", {
              correct: result.greenIndices.length,
              total: order.length,
              points: result.totalPoints,
            })}
          </p>
          <RevealList
            entries={revealEntries}
            correctOrder={correctOrder}
            showCorrectOrderLabel={t("timeslip.play.showCorrectOrder")}
            showMyOrderLabel={t("timeslip.play.showMyOrder")}
          />
        </>
      )}

      <div className={styles.actions}>
        {!checked ? (
          <button type="button" className={styles.primaryButton} onClick={handleCheck}>
            {t("timeslip.play.check")}
          </button>
        ) : (
          <button type="button" className={styles.primaryButton} onClick={handleNext}>
            {t("timeslip.play.next")}
          </button>
        )}
      </div>
      <Link to="/" className={styles.cancelLink}>
        {t("timeslip.play.cancelGame")}
      </Link>
    </main>
  );
}
